/**
 * NgwEditorMailExtensions — `IMailEditorExtensions` 의 NgwEditor 구현 (PR-N4 + PR-N5).
 *
 * 메일 도메인 전용 표면을 단일 객체로 묶어 노출 — TinyMCEMailExtensions (PR-7b) 의 평행 구현.
 *
 * - `region`  : 4-region aggregator (nBody / signature / quotedHeader / quotedBody / quotedButton)
 * - `insert`  : PR-N5 본 구현 — `NgwEditorMailInsert` (5 메서드 모두 어댑터 명령 위임)
 * - cidMap / acex matches / nBodyCounter — 사용자 결정 (a): 클래스 멤버로 흡수. PR-7b 의
 *   `TinyMCEMailExtensions` 가 글로벌 (`editorMgr.cidMap` / `editorConfig.acex.matches` /
 *   `editorMgr.nineBodyID`) 에 위임하는 것과 평행. NgwEditor 측에는 글로벌이 없으므로 어댑터
 *   인스턴스 멤버로 보존.
 *
 * ### nBodyCounter 의 결정성
 *
 * TinyMCE 의 `editorMgr.nineBodyID = 'nine_body_' + getMailID()` 는 글로벌 카운터 (mailID)
 * 와 결합. NgwEditor 에서는 어댑터 인스턴스마다 0 부터 시작 — 동일 어댑터 인스턴스 안에서 여러
 * 본문이 생성될 때 단조 증가. EmailForm 이 EditorAdapter 를 본문 1개당 1 인스턴스로 사용하므로
 * 단조성만 보장하면 됨.
 *
 * ### cidMap 의 표면 변환
 *
 * `getCidMap()` / `setCidMap()` 은 외부 표면 `Map<string, string>` 으로 통일 (PR-7b 의
 * `TinyMCEMailExtensions` 와 동일). 내부 저장도 Map — TinyMCE 의 Object 인덱서 backwards
 * compatible 변환은 NgwEditor 에는 불필요.
 *
 * EmailForm/SignatureEdit 의 호출부는 `editor.mail.region.*` / `editor.mail.setCidMap(...)` /
 * `editor.mail.checkData()` / `editor.mail.resetAcexMatches()` / `editor.mail.getNBodyID()` 의
 * 단일 표면을 사용한다.
 */

import type { IEditorMailRegion } from '../IEditorMailRegion'
import type { IMailEditorExtensions } from '../IEditor'
import type { NgwEditorAdapter } from './NgwEditorAdapter'
import { NgwEditorMailInsert } from './NgwEditorMailInsert'
import { NgwEditorMailRegion } from './NgwEditorMailRegion'
import { NgwEditorQuotedButton } from './NgwEditorQuotedButton'

/**
 * 4 region + quotedButton 의 aggregator. TinyMCEMailRegion 의 평행 구현.
 */
class NgwEditorMailRegionAggregator implements IEditorMailRegion {
  readonly nBody: NgwEditorMailRegion
  readonly blankSign: NgwEditorMailRegion
  readonly signature: NgwEditorMailRegion
  readonly blankMsg: NgwEditorMailRegion
  readonly quotedHeader: NgwEditorMailRegion
  readonly quotedBody: NgwEditorMailRegion
  readonly quotedButton: NgwEditorQuotedButton

  constructor(adapter: NgwEditorAdapter) {
    this.nBody = new NgwEditorMailRegion(adapter, 'nBody')
    this.blankSign = new NgwEditorMailRegion(adapter, 'blankSign')
    this.signature = new NgwEditorMailRegion(adapter, 'signature')
    this.blankMsg = new NgwEditorMailRegion(adapter, 'blankMsg')
    this.quotedHeader = new NgwEditorMailRegion(adapter, 'quotedHeader')
    this.quotedBody = new NgwEditorMailRegion(adapter, 'quotedBody')
    this.quotedButton = new NgwEditorQuotedButton(adapter)
  }
}

export class NgwEditorMailExtensions implements IMailEditorExtensions {
  readonly region: NgwEditorMailRegionAggregator
  readonly insert: NgwEditorMailInsert

  /** CID → URL 매핑. PR-7b 의 `editorMgr.cidMap` 평행 — 어댑터 멤버로 흡수. */
  private cidMap: Map<string, string> = new Map()

  /**
   * 자동완성 (acex) 매칭 캐시. PR-7b 의 `editorConfig.acex.matches` 평행. 본 PR 단계에서는
   * 외부 표면이 `resetAcexMatches()` 의 NoOp 매핑만 필요 — 매칭 데이터 자체의 형태는 PR-N6
   * 의 event handler wiring 에서 확정된 후 보강.
   */
  private acexMatches: unknown = null

  /**
   * nBody ID 카운터. PR-7b 의 `editorMgr.nineBodyID` 평행. 어댑터 인스턴스마다 0 부터 단조 증가.
   * `getNBodyID()` 호출 시 `'nine_body_n<N>'` 포맷 반환 (legacy TinyMCE 와 동일 prefix).
   */
  private nBodyCounter = 0

  constructor(adapter: NgwEditorAdapter) {
    this.region = new NgwEditorMailRegionAggregator(adapter)
    this.insert = new NgwEditorMailInsert(adapter)
  }

  getCidMap(): Map<string, string> {
    // 호출자가 변경해도 어댑터 상태에 영향 없도록 새 Map 반환 (PR-7b 와 동일 시맨틱).
    return new Map(this.cidMap)
  }

  setCidMap(map: Map<string, string>): void {
    this.cidMap = new Map(map)
  }

  checkData(_opts?: { restoreFold?: boolean }): void {
    // PR-N6 의 event handler wiring 단계에서 본문 정합성 체크 (이미지/링크/inline 첨부 일관성)
    // 본 구현 도입. 현재는 NoOp — TinyMCE 의 editorMgr.checkData 가 cidMap/acex 와 결합되어 있어
    // NgwEditor 측 본 구현은 cidMap 의 src 매칭 단계와 함께 짜야 한다.
  }

  resetAcexMatches(): void {
    this.acexMatches = null
  }

  getNBodyID(): string {
    // 호출 시점마다 단조 증가 — TinyMCE 의 nineBodyID 가 setNBody 호출 시점에 갱신되는 것과 동일.
    this.nBodyCounter += 1
    return `nine_body_n${this.nBodyCounter}`
  }

  /** 테스트 / 디버깅 보조. @internal */
  peekNBodyCounter(): number {
    return this.nBodyCounter
  }

  /** 테스트 / 디버깅 보조. @internal */
  peekAcexMatches(): unknown {
    return this.acexMatches
  }
}
